/**
 * ============================================
 * AUTH CONTEXT
 * ============================================
 * 
 * Provides authentication state and methods using AWS Cognito.
 * Implements OAuth2 PKCE flow manually for reliable token exchange.
 * 
 * FLOW:
 * 1. User clicks login → redirects to Cognito Hosted UI with PKCE challenge
 * 2. After login, Cognito redirects back with ?code=xxx
 * 3. App exchanges code for tokens using the stored code verifier
 * 4. Tokens are stored in localStorage and user is authenticated
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { cognitoConfig } from '@/config';

// ============================================
// Types
// ============================================

interface User {
  username: string;
  email?: string;
  groups: string[];
  attributes: Record<string, unknown>;
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  /**
   * True when the page is running inside the no-login "Lokko Monitor"
   * Android APK. UI should hide the logout button and any auth-related
   * affordances when this is true -- the variant is designed to be
   * permanently signed in as a monitor-role user.
   */
  isMonitorApp: boolean;
}

// ============================================
// Monitor-app detection (no-login Android variant)
// ============================================

/**
 * The Monitor APK signals itself two ways:
 *   1. window.__LOKKO_APP_EDITION__ === 'monitor'   (set by WebView before
 *                                                    any page script runs)
 *   2. AndroidBridge.getAppEdition() === 'monitor'  (defence-in-depth in case
 *                                                    the page reloads after
 *                                                    the eval injection)
 *   3. ?app=monitor query string                    (final fallback; survives
 *                                                    a hard reload even if the
 *                                                    bridge isn't attached yet)
 *
 * Any one of the three is enough. The presence of the bridge is what
 * meaningfully distinguishes "real APK" from "URL-tampering in a browser";
 * a browser has no AndroidBridge, so even if someone types ?app=monitor
 * into the address bar of overseer.lokkobackendorg.com they still go through
 * normal Cognito (the query-string-only path only matches when AndroidBridge
 * is also present -- see logic below).
 */
type AndroidBridgeLike = {
  getAppEdition?: () => string;
};

declare global {
  interface Window {
    __LOKKO_APP_EDITION__?: string;
    AndroidBridge?: AndroidBridgeLike;
  }
}

function detectMonitorApp(): boolean {
  if (typeof window === 'undefined') return false;

  // Strong signals -- either alone is conclusive proof we're inside the
  // monitor APK (these globals don't exist in a regular browser).
  if (window.__LOKKO_APP_EDITION__ === 'monitor') return true;
  try {
    if (window.AndroidBridge?.getAppEdition?.() === 'monitor') return true;
  } catch {
    // Bridge call can throw on older WebView builds; ignore.
  }

  // Weak signal: ?app=monitor query string. Only honour this when the
  // AndroidBridge is also attached (i.e. we're definitely inside SOME
  // Lokko APK). This stops a browser user from bypassing Cognito by
  // adding ?app=monitor to the URL.
  if (
    window.AndroidBridge &&
    new URLSearchParams(window.location.search).get('app') === 'monitor'
  ) {
    return true;
  }

  return false;
}

/**
 * Synthetic user used by the Monitor APK. groups=['monitor'] feeds
 * directly into getEffectiveRole() which already returns 'monitor' --
 * so every existing role-gated piece of UI works without further changes.
 */
const MONITOR_APP_USER: User = {
  username: 'monitor-app',
  email: undefined,
  groups: ['monitor'],
  attributes: { source: 'monitor-app', synthetic: true },
};

// ============================================
// PKCE Helper Functions
// ============================================

function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => charset[byte % charset.length]).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function parseJwt(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT:', e);
    return {};
  }
}

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEYS = {
  CODE_VERIFIER: 'lokko_code_verifier',
  ACCESS_TOKEN: 'lokko_access_token',
  ID_TOKEN: 'lokko_id_token',
  REFRESH_TOKEN: 'lokko_refresh_token',
  TOKEN_EXPIRY: 'lokko_token_expiry',
};

// ============================================
// Context
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================
// Provider
// ============================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Computed once at module load. Safe -- the WebView injects
  // window.__LOKKO_APP_EDITION__ via evaluateJavascript on onPageStarted,
  // which runs before any of our React bundle executes.
  const isMonitorApp = detectMonitorApp();

  // Monitor APK: synthesise the user immediately, skip the loading
  // spinner entirely (there's no async work to do -- no Cognito, no
  // localStorage lookup). All the auth-flow effects below short-circuit
  // when isMonitorApp is true.
  const [user, setUser] = useState<User | null>(isMonitorApp ? MONITOR_APP_USER : null);
  const [isLoading, setIsLoading] = useState(!isMonitorApp);
  const [error, setError] = useState<string | null>(null);
  const isProcessingCallback = useRef(false);

  /**
   * Exchange authorization code for tokens
   */
  const exchangeCodeForTokens = async (code: string): Promise<TokenResponse | null> => {
    const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
    
    if (!codeVerifier) {
      console.error('❌ No code verifier found in sessionStorage');
      setError('Authentication failed: No code verifier found. Please try logging in again.');
      return null;
    }

    const tokenEndpoint = `https://${cognitoConfig.oauth.domain}/oauth2/token`;
    
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognitoConfig.userPoolClientId,
      code: code,
      redirect_uri: cognitoConfig.oauth.redirectSignIn,
      code_verifier: codeVerifier,
    });

    console.log('🔄 Exchanging code for tokens...');
    console.log('   Token endpoint:', tokenEndpoint);
    console.log('   Redirect URI:', cognitoConfig.oauth.redirectSignIn);

    try {
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        console.error('❌ Token exchange failed:', response.status, responseText);
        setError(`Token exchange failed: ${responseText}`);
        return null;
      }

      const tokens: TokenResponse = JSON.parse(responseText);
      console.log('✅ Tokens received successfully');
      
      // Store tokens
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
      localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
      if (tokens.refresh_token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      }
      localStorage.setItem(
        STORAGE_KEYS.TOKEN_EXPIRY,
        String(Date.now() + tokens.expires_in * 1000)
      );
      
      // Clear the code verifier
      sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
      
      return tokens;
    } catch (err) {
      console.error('❌ Error exchanging code for tokens:', err);
      setError(`Token exchange error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return null;
    }
  };

  /**
   * Extract user info from ID token
   */
  const getUserFromToken = (idToken: string): User | null => {
    const payload = parseJwt(idToken);
    
    if (!payload || !payload.sub) {
      console.error('❌ Invalid token payload');
      return null;
    }

    console.log('✅ User extracted from token:', payload['cognito:username'] || payload.sub);

    return {
      username: (payload['cognito:username'] as string) || (payload.sub as string),
      email: payload.email as string | undefined,
      groups: (payload['cognito:groups'] as string[]) || [],
      attributes: payload,
    };
  };

  /**
   * Refresh access/id tokens using the stored refresh_token. Cognito does
   * NOT return a new refresh_token here -- the existing one stays valid
   * until its own expiration (default 30 days, set in Cognito App Client).
   *
   * Returns:
   *   {tokens, status: 'ok'}        — refresh succeeded, new tokens stored
   *   {tokens: null, status: 'rejected'}  — Cognito explicitly rejected
   *       the refresh token (expired/revoked). Caller MUST force a full
   *       re-login.
   *   {tokens: null, status: 'transient'} — network error or 5xx from
   *       Cognito. Refresh token might still be valid; caller should
   *       NOT log the user out -- just retry on the next tick. This
   *       prevents a 1-minute network blip from silently logging users
   *       out of an otherwise-valid session.
   */
  type RefreshResult =
    | { tokens: TokenResponse; status: 'ok' }
    | { tokens: null; status: 'rejected' | 'transient' };

  const refreshTokens = async (): Promise<RefreshResult> => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      // No refresh token at all -- treat as rejected so caller logs out.
      return { tokens: null, status: 'rejected' };
    }

    const tokenEndpoint = `https://${cognitoConfig.oauth.domain}/oauth2/token`;
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cognitoConfig.userPoolClientId,
      refresh_token: refreshToken,
    });

    console.log('🔄 Attempting silent token refresh...');
    let response: Response;
    try {
      response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
    } catch (err) {
      // Network error -- could be flaky wifi, captive portal, etc.
      console.warn('⚠️ Token refresh network error -- will retry next tick:', err);
      return { tokens: null, status: 'transient' };
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      // 4xx = Cognito rejected (refresh token revoked/expired). 5xx = server
      // problem on Cognito's side; refresh token might still be good.
      if (response.status >= 500) {
        console.warn(`⚠️ Cognito ${response.status} -- treating as transient: ${text}`);
        return { tokens: null, status: 'transient' };
      }
      console.warn(`⚠️ Refresh token rejected by Cognito (${response.status}): ${text}`);
      return { tokens: null, status: 'rejected' };
    }

    const tokens: TokenResponse = await response.json();
    // Cognito does not rotate refresh_token on refresh -- keep the old one
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
    localStorage.setItem(
      STORAGE_KEYS.TOKEN_EXPIRY,
      String(Date.now() + (tokens.expires_in || 3600) * 1000)
    );
    console.log(`✅ Token refresh succeeded (next expiry in ${tokens.expires_in}s)`);
    return { tokens, status: 'ok' };
  };

  /**
   * Wipe all auth state from localStorage. Called when refresh genuinely
   * fails (refresh_token expired/revoked) -- user has to log in again.
   */
  const clearStoredTokens = () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  };

  /**
   * Check if stored tokens are valid. If the access/id token is expired
   * but a refresh_token exists, silently refresh before falling back to
   * forcing a full login. This is what keeps users signed in across
   * app/tab restarts -- previously this method just deleted everything
   * on expiry, so every restart > 1h after login forced a re-login.
   */
  const checkStoredAuth = useCallback(async (): Promise<User | null> => {
    const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
    const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

    if (!idToken || !expiry) {
      console.log('ℹ️ No stored tokens found');
      return null;
    }

    // Check if token is expired (with 60 second buffer)
    if (Date.now() > parseInt(expiry, 10) - 60000) {
      console.log('ℹ️ Stored access token expired -- attempting refresh');
      const result = await refreshTokens();
      if (result.status === 'ok') {
        return getUserFromToken(result.tokens.id_token);
      }
      if (result.status === 'rejected') {
        // Refresh token genuinely expired/revoked -- wipe and force login.
        console.log('ℹ️ Refresh rejected -- clearing stored auth');
        clearStoredTokens();
        return null;
      }
      // Transient (network/5xx) -- preserve tokens; the user might have
      // a valid session, we just can't verify right now. Render the stale
      // user from the (expired) id_token so the app stays usable; next
      // refresh tick will re-attempt.
      console.warn('⚠️ Refresh transient failure -- showing stale user, will retry');
      return getUserFromToken(idToken);
    }

    return getUserFromToken(idToken);
  }, []);

  /**
   * Redirect to Cognito Hosted UI for login (with PKCE).
   *
   * Hard no-op in the Monitor APK. The "never signs out / no login" spec
   * means there is no legitimate reason to ever bounce that variant to
   * Cognito; guarding here closes the back door if any future code path
   * (a stray button, a regression in the /login route guard, a recovery
   * flow) calls login() in monitor mode. The synthetic user already in
   * state is the only auth state that variant ever needs.
   */
  const login = useCallback(async () => {
    if (isMonitorApp) {
      console.log('🔒 login() ignored -- Monitor APK has no Cognito flow');
      return;
    }
    try {
      setError(null);
      console.log('🔐 Starting login flow...');
      
      // Generate PKCE values
      const codeVerifier = generateRandomString(128);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // Store code verifier for later
      sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
      console.log('   Code verifier stored in sessionStorage');
      
      // Build authorization URL
      const authUrl = new URL(`https://${cognitoConfig.oauth.domain}/oauth2/authorize`);
      authUrl.searchParams.set('client_id', cognitoConfig.userPoolClientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', cognitoConfig.oauth.scopes.join(' '));
      authUrl.searchParams.set('redirect_uri', cognitoConfig.oauth.redirectSignIn);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');
      
      console.log('   Redirecting to:', authUrl.toString());
      
      // Redirect to Cognito
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(`Login failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [isMonitorApp]);

  /**
   * Sign out and redirect to Cognito logout.
   *
   * Hard no-op in the Monitor APK: the whole point of that variant is
   * that it never signs out. UI shouldn't render a logout button in this
   * mode either (see Dashboard), but defending here means a stray
   * `logout()` call from anywhere can't strand the on-call person on
   * a Cognito redirect.
   */
  const logout = useCallback(() => {
    if (isMonitorApp) {
      console.log('🔒 logout() ignored -- Monitor APK does not sign out');
      return;
    }
    console.log('🚪 Logging out...');

    // Clear all stored tokens
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);

    // Clear user state
    setUser(null);
    setError(null);

    // Build logout URL
    const logoutUrl = new URL(`https://${cognitoConfig.oauth.domain}/logout`);
    logoutUrl.searchParams.set('client_id', cognitoConfig.userPoolClientId);
    logoutUrl.searchParams.set('logout_uri', cognitoConfig.oauth.redirectSignOut);

    // Redirect to Cognito logout
    window.location.href = logoutUrl.toString();
  }, [isMonitorApp]);

  /**
   * Initialize auth state - check for callback code or existing tokens.
   *
   * Skipped entirely in the Monitor APK: the synthetic user was already
   * set in useState's initial value, so there's no Cognito callback to
   * process, no tokens to refresh, no loading state to clear.
   */
  useEffect(() => {
    if (isMonitorApp) return;
    const initAuth = async () => {
      // Prevent double processing
      if (isProcessingCallback.current) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      // Handle OAuth error
      if (errorParam) {
        console.error('❌ OAuth error:', errorParam, errorDescription);
        setError(errorDescription || errorParam);
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoading(false);
        return;
      }
      
      // Handle OAuth callback with code
      if (code) {
        isProcessingCallback.current = true;
        console.log('📥 Authorization code received:', code.substring(0, 20) + '...');
        
        // Clear the code from URL immediately
        window.history.replaceState({}, document.title, window.location.pathname);
        
        const tokens = await exchangeCodeForTokens(code);
        
        if (tokens) {
          const userData = getUserFromToken(tokens.id_token);
          if (userData) {
            setUser(userData);
            console.log('✅ Authentication successful!');
          } else {
            setError('Failed to parse user information from token');
          }
        }
        
        isProcessingCallback.current = false;
        setIsLoading(false);
        return;
      }
      
      // No code in URL - check for existing valid tokens (or silently refresh)
      console.log('🔍 Checking for existing authentication...');
      const existingUser = await checkStoredAuth();
      if (existingUser) {
        console.log('✅ Found valid existing session');
        setUser(existingUser);
      } else {
        console.log('ℹ️ No valid session found');
      }

      setIsLoading(false);
    };

    initAuth();
  }, [checkStoredAuth, isMonitorApp]);

  /**
   * Proactive token refresh while the app is open. Without this, an idle
   * tab/app would let the access token expire silently; the next backend
   * call would 401 and the user would be bounced to Cognito mid-session.
   * Runs every 5 minutes; refreshes if the access token is within 10 min
   * of expiring. If refresh fails (refresh_token revoked), logs the user
   * out cleanly so the next action sees the unauthenticated state.
   */
  useEffect(() => {
    if (!user) return;
    // Monitor APK has no tokens to refresh -- there is no Cognito session
    // at all, just a synthetic user. Skipping the timer also closes the
    // failure mode where a transient refresh-rejection could somehow
    // clear the synthetic user.
    if (isMonitorApp) return;
    const interval = setInterval(async () => {
      const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
      if (!expiry) return;
      const msUntilExpiry = parseInt(expiry, 10) - Date.now();
      if (msUntilExpiry < 10 * 60 * 1000) {
        const result = await refreshTokens();
        if (result.status === 'rejected') {
          // Cognito explicitly rejected -- refresh_token is dead.
          console.warn('⚠️ Refresh rejected -- session no longer valid');
          clearStoredTokens();
          setUser(null);
        }
        // 'transient' (network/5xx) -- do NOTHING. User stays signed in;
        // next 5-min tick retries. A network blip must not log the user out.
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, isMonitorApp]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    isMonitorApp,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default AuthContext;
